import { SimpleSelect } from '@/components/form/inputs/SimpleSelect';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card/Card';
import { Label } from '@/components/ui/label';
import { FilterX } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';

type FilterControlsProps = {
  machineOptions: string[];
  materialOptions: string[];
  operationOptions: string[];
  selectedMachine: string;
  selectedMaterial: string;
  selectedOperation: string;
  onMachineChange: (value: string) => void;
  onMaterialChange: (value: string) => void;
  onOperationChange: (value: string) => void;
  onClearFilters: () => void;
};

export function FilterControls({
  machineOptions,
  materialOptions,
  operationOptions,
  selectedMachine,
  selectedMaterial,
  selectedOperation,
  onMachineChange,
  onMaterialChange,
  onOperationChange,
  onClearFilters,
}: FilterControlsProps) {
  const { t } = useTranslation();

  const hasActiveFilters =
    selectedMachine !== '__all__' ||
    selectedMaterial !== '__all__' ||
    selectedOperation !== '__all__';

  const filters = [
    {
      id: 'comparison-filter-machine',
      key: 'machine',
      value: selectedMachine,
      options: machineOptions,
      onChange: onMachineChange,
    },
    {
      id: 'comparison-filter-material',
      key: 'material',
      value: selectedMaterial,
      options: materialOptions,
      onChange: onMaterialChange,
    },
    {
      id: 'comparison-filter-operation',
      key: 'operation',
      value: selectedOperation,
      options: operationOptions,
      onChange: onOperationChange,
    },
  ] as const;

  return (
    <Card as="section" padding="lg">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="eyebrow">{t('analytics.comparison.layout.filters.title')}</h3>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" className="gap-2" onClick={onClearFilters}>
              <FilterX aria-hidden />
              {t('analytics.comparison.layout.filters.clear')}
            </Button>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filters.map((filter) => (
            <div key={filter.key} className="space-y-2">
              <Label htmlFor={filter.id} className="text-sm font-medium">
                {t(`analytics.comparison.layout.filters.${filter.key}.label`)}
              </Label>
              <SimpleSelect
                id={filter.id}
                value={filter.value}
                onChange={(value) => filter.onChange(value ?? '__all__')}
                options={[
                  {
                    value: '__all__',
                    label: t(`analytics.comparison.layout.filters.${filter.key}.all`),
                  },
                  ...filter.options.map((option) => ({ value: option, label: option })),
                ]}
                placeholder={t(`analytics.comparison.layout.filters.${filter.key}.placeholder`)}
              />
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}
